import { pageSchemaScript } from "@/seo/schema";
import { createFileRoute } from "@tanstack/react-router";
import { SECTION_OG, sectionImageMeta } from "@/lib/ogSections";
import { PageShell, PageHero } from "@/components/PageShell";
import { SectionHeader } from "@/components/SectionHeader";
import { Reveal } from "@/components/Reveal";

const GROUPS = [
  { title: "OEM Ecosystem", desc: "Long-term relationships with leading global manufacturers across CCTV, perimeter, access control, fire alarm, audio, video, lighting and control. Brand references shared selectively based on project relevance." },
  { title: "Technology Partners", desc: "Integration partners across VMS, automation platforms, networking and AI analytics — chosen by capability fit, not commercial alignment." },
  { title: "Architect Ecosystem", desc: "Trusted collaboration with India's leading architecture and interior design practices on luxury residences, hospitality, corporate and institutional projects." },
  { title: "Consultants", desc: "Co-design with security, acoustic, MEP and IT consultants when project scope requires multi-discipline coordination." },
  { title: "Integration Partners", desc: "Execution partners across India for kilometre-scale perimeter, multi-site rollouts, and portfolio-level deployments with documented SLAs." },
];

const PARTNERS_TITLE = "Partners — OEM & Integration Ecosystem | Arif Khan";
const PARTNERS_DESC =
  "Partner ecosystem and OEM references for security, automation, AV, and intelligent building projects — shared selectively by project relevance.";

export const Route = createFileRoute("/insights/partners")({
  component: PartnersPage,
  head: () => ({
    meta: [
      { title: PARTNERS_TITLE },
      { name: "description", content: PARTNERS_DESC },
      { property: "og:title", content: PARTNERS_TITLE },
      { property: "og:description", content: "OEM and integration partner ecosystem for security, automation, AV and intelligent buildings." },
      { property: "og:url", content: "https://arifkhanglobal.com/insights/partners" },
      ...sectionImageMeta(SECTION_OG.askArif, "Partners — Arif Khan"),
    ],
    links: [
      { rel: "canonical", href: "https://arifkhanglobal.com/insights/partners" },
    ],
    scripts: [
      pageSchemaScript({
        path: "/insights/partners",
        type: "CollectionPage",
        name: PARTNERS_TITLE,
        description: PARTNERS_DESC,
        breadcrumb: [
          { name: "Insights", url: "/insights" },
          { name: "Partners", url: "/insights/partners" },
        ],
      }),
    ],

  }),
});

function PartnersPage() {
  return (
    <PageShell>
      <PageHero
        eyebrow="Partners"
        title="A vendor-neutral ecosystem, by design."
        description="OEM and project partner references are shared selectively based on project relevance, integration requirement, serviceability, and confidentiality."
      />
      <section className="py-16 border-b border-border bg-surface">
        <div className="container-page max-w-3xl">
          <p className="text-base md:text-lg text-ink-soft leading-relaxed">
            We do not publish a list of OEM logos. Brand and integration partner references are shared with clients when a project's technical brief, serviceability requirement, and confidentiality scope allow — and only where the partnership materially benefits the program.
          </p>
        </div>
      </section>
      <section className="py-24">
        <div className="container-page grid gap-px bg-border border border-border rounded-2xl overflow-hidden md:grid-cols-2 lg:grid-cols-3">
          {GROUPS.map((g, i) => (
            <Reveal key={g.title} delay={i * 0.05}>
              <div className="bg-background p-10 h-full">
                <SectionHeader eyebrow={`Group 0${i + 1}`} title={g.title} />
                <p className="mt-4 text-ink-soft leading-relaxed">{g.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </PageShell>
  );
}
